import React from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { COLORS_ADMIN_PAGE, ResizeHandle, StyledSidebar } from '../conf/constants';
import { TRANSACTION_MANAGEMENT_CONSTANTS } from './constants/transactionManagement';
import { useErrorHandler } from '../utils/useErrorHandler';
import { useUsers, useTransactions, useResize } from './hooks';
import { UserSidebar, TransactionTable, CreditAdjustmentModal } from './components';

const TransactionManagement: React.FC = () => {
  const { snackbar, showSuccessMessage, showErrorMessage, handleSnackbarClose } = useErrorHandler();
  const { sidebarWidth, isResizing, containerRef, handleMouseDown } = useResize(
    TRANSACTION_MANAGEMENT_CONSTANTS.DEFAULT_SIDEBAR_WIDTH
  );

  const {
    users,
    selectedUser,
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm,
    currentPage: userCurrentPage,
    totalUsers,
    usersPerPage,
    handlePageChange: handleUserPageChange,
    handleUserSelect,
    fetchUsers,
    onUsersPerPageChange,
  } = useUsers();

  const {
    transactions,
    searchTerm: transactionSearchTerm,
    setSearchTerm: setTransactionSearchTerm,
    currentPage: transactionCurrentPage,
    totalTransactions,
    transactionsPerPage,
    handlePageChange: handleTransactionPageChange,
    adjustCredits,
    onTransactionsPerPageChange, 
  } = useTransactions(selectedUser?.userId);

  const [openCreditModal, setOpenCreditModal] = React.useState(false);

  const handleCreditAdjustment = async (adjustment: any) => {
    const success = await adjustCredits(adjustment);
    if (success) {
      fetchUsers(); 
      showSuccessMessage(TRANSACTION_MANAGEMENT_CONSTANTS.MESSAGES.CREDIT_ADJUSTMENT_SUCCESS);
    } else {
      showErrorMessage(TRANSACTION_MANAGEMENT_CONSTANTS.MESSAGES.CREDIT_ADJUSTMENT_FAILED);
    }
    
    return success;
  };

  const handleOpenCreditModal = () => {
    if (selectedUser) {
      setOpenCreditModal(true);
    }
  };

  return (
    <Box
      sx={{ 
        display: 'flex', 
        bgcolor: COLORS_ADMIN_PAGE.BACKGROUND, 
        height: TRANSACTION_MANAGEMENT_CONSTANTS.CONTAINER_HEIGHT 
      }}
      ref={containerRef}
      style={{ cursor: isResizing ? 'col-resize' : 'auto' }}
    >
      <StyledSidebar elevation={0} sidebarWidth={sidebarWidth}>
        <UserSidebar
          users={users}
          selectedUser={selectedUser}
          searchTerm={userSearchTerm}
          onSearchChange={setUserSearchTerm}
          onUserSelect={handleUserSelect}
          currentPage={userCurrentPage}
          totalUsers={totalUsers}
          usersPerPage={usersPerPage}
          onPageChange={handleUserPageChange}
          onUsersPerPageChange={onUsersPerPageChange}
        />
      </StyledSidebar>

      <ResizeHandle onMouseDown={handleMouseDown} />

      <Box sx={{ flex: 1, bgcolor: COLORS_ADMIN_PAGE.WHITE, display: 'flex', flexDirection: 'column' }}>
        {selectedUser ? (
          <TransactionTable
            transactions={transactions}
            searchTerm={transactionSearchTerm}
            onSearchChange={setTransactionSearchTerm}
            currentPage={transactionCurrentPage}
            totalTransactions={totalTransactions}
            transactionsPerPage={transactionsPerPage}
            onPageChange={handleTransactionPageChange}
            onCreditAdjustmentClick={handleOpenCreditModal}
            onTransactionsPerPageChange={onTransactionsPerPageChange} 
          />
        ) : (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center', 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Typography variant="h6" color="textSecondary">
              {TRANSACTION_MANAGEMENT_CONSTANTS.MESSAGES.NO_USER_SELECTED}
            </Typography>
          </Box>
        )}
      </Box>

      <CreditAdjustmentModal
        open={openCreditModal}
        onClose={() => setOpenCreditModal(false)}
        onSubmit={handleCreditAdjustment}
        selectedUser={selectedUser}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={TRANSACTION_MANAGEMENT_CONSTANTS.SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionManagement;